import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const PATH = "/en/heritage/kunskapsarv";
const SV_PATH = "/heritage/kunskapsarv";

const USE_CASES = [
  "Knowledge transfer before retirement",
  "Interview with a founder",
  "Documenting company culture",
  "Onboarding material",
  "Internal training",
  "Generational handover",
  "Company history",
  "Documentation of methods and experience",
];

const PROCESS = [
  { label: "First conversation", body: "We understand the purpose, which knowledge should be documented and how the material will be used internally." },
  { label: "Interview design", body: "Question areas and structure are developed together with you, tailored to the knowledge domains and audience." },
  { label: "Filmed interview", body: "One or more interviews are recorded at the office, in a studio or in the environment where the work is performed." },
  { label: "Editing and chapter structure", body: "The material is edited into a main film and shorter parts per knowledge area — useful in onboarding and training." },
  { label: "Delivery", body: "Films are delivered digitally for internal use. We can also package material for intranet or LMS." },
];

const PACKAGES = [
  { name: "Key Person Interview", body: "For a senior employee, specialist, leader or founder. Includes pre-meeting, interview design, filmed interview, editing and internal delivery." },
  { name: "Knowledge Transfer", body: "For organisations that need to document tacit knowledge, methods and experience. Interview structure, knowledge areas, filmed interview or interview series and chaptered material." },
  { name: "Company Legacy", body: "For companies that want to document history, culture, founder journey or generational handover. Interviews with several people, a summary film and shorter internal clips." },
];

const VALUE = [
  "Reduces the risk of losing critical experience when people leave the organisation",
  "Supports onboarding and internal training",
  "Documents culture, values and working methods",
  "Preserves founder stories and company memory",
  "Useful for generational handovers, retirements and anniversaries",
  "Makes tacit knowledge accessible to more people in the organisation",
];

const FAQ = [
  { q: "What is a knowledge legacy?", a: "A knowledge legacy is structured filmed documentation of experience, methods and tacit knowledge inside an organisation. The material is used for onboarding, internal training and as preserved company memory." },
  { q: "How do you document tacit knowledge?", a: "Through structured interviews where the right questions open up concrete examples, decision logic and experience. Filmed material preserves both content and context — body language, tone and narrative style." },
  { q: "How do you transfer knowledge when someone retires?", a: "By interviewing the person well before they leave, documenting methods, decision models and key experience, and editing the material into chapters that successors and new employees can use." },
  { q: "Which roles are suitable for the interviews?", a: "Founders, senior specialists, leaders, project owners, people with long experience in the core business — or entire teams that together carry a working method." },
  { q: "How is the material used internally?", a: "Most common uses are onboarding new employees, internal training, intranet, culture work, anniversaries and as a reference at generational handovers." },
  { q: "What does Knowledge Transfer cost?", a: "Price depends on the number of people, scope and how much material is edited. We provide a quote after a first conversation where we understand the need." },
];

export const Route = createFileRoute("/en_/heritage/kunskapsarv")({
  head: () => ({
    meta: [
      { title: "Knowledge Legacy — expert knowledge on film | Fenny" },
      { name: "description", content: "Filmed documentation of experience, methods and tacit knowledge for companies and organisations. Onboarding, generational handover, founder stories and company memory." },
      { property: "og:title", content: "Knowledge Legacy on film — Fenny Heritage" },
      { property: "og:description", content: "Structured filmed interviews that preserve experience and tacit knowledge inside the organisation." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [
      { rel: "canonical", href: `https://fenny.se${PATH}` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/heritage/kunskapsarv", name: "Knowledge Legacy — document tacit knowledge on film | Fenny Heritage", description: "Filmed documentation of experience, methods and tacit knowledge for companies and organisations. Onboarding, generational handover, founder stories and company memory."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Knowledge Legacy on film",
            path: PATH,
            description:
              "Fenny Heritage produces structured filmed interviews for companies and organisations that want to document experience, methods, tacit knowledge and company history.",
            serviceType: "Filmed knowledge transfer and company documentation",
            priceRange: "Quote after first conversation",
            areaServed: ["Sweden"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Home", path: "/en" },
            { name: "Heritage", path: "/en/heritage" },
            { name: "Knowledge Legacy", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: KnowledgeLegacyPage,
});

function KnowledgeLegacyPage() {
  return (
    <article className="pt-28">
      <Section className="pb-10">
        <div className="max-w-4xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">Heritage Business</div>
          <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
            Knowledge Legacy — document experience and tacit knowledge
          </h1>
          <p className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl" data-speakable>
            Structured filmed interviews with key people, founders and senior
            staff. The material is used for onboarding, internal training,
            culture work, anniversaries and documentation.
          </p>
          <QuickAnswer
            className="mt-6"
            text="A knowledge legacy is filmed documentation of experience, methods and tacit knowledge inside an organisation — preserved and packaged for onboarding, internal training and as company memory."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/en/contact" className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-background hover:opacity-90">
              Book a first conversation <ArrowRight className="size-4" />
            </Link>
            <Link to="/en/heritage" className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-3 text-sm font-medium hover:bg-foreground/[0.05]">
              Back to Heritage
            </Link>
          </div>
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Value for the organisation</h2>
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {VALUE.map((v) => (
            <li key={v} className="flex gap-3 rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-foreground/85">
              <span className="mt-2 size-1.5 shrink-0 rounded-full bg-mint" />
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Examples of use</h2>
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {USE_CASES.map((u) => (
            <li key={u} className="rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-foreground/85">{u}</li>
          ))}
        </ul>
      </Section>

      <Section className="py-10" id="process">
        <h2 className="text-3xl font-semibold md:text-4xl">How it works</h2>
        <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {PROCESS.map((s, i) => (
            <li key={s.label} className="rounded-2xl border border-white/10 bg-surface/40 p-5">
              <div className="text-xs uppercase tracking-widest text-mint">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-medium">{s.label}</div>
              <p className="mt-2 text-sm text-foreground/75">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section className="py-10" id="packages">
        <h2 className="text-3xl font-semibold md:text-4xl">Packages</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {PACKAGES.map((p) => (
            <div key={p.name} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
              <div className="font-medium">{p.name}</div>
              <p className="mt-3 text-sm text-foreground/75">{p.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-foreground/65">Pricing is provided as a quote after the first conversation.</p>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Frequently asked questions</h2>
        <div className="mt-8 max-w-3xl space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/75">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <RelatedLinksRail sourcePath={PATH} />
      <SignatureCta />
    </article>
  );
}
